/**
 * Unified View Rules Model
 *
 * This module defines a unified model for view rules that combines
 * filtering (search) and display rules (styling) into a single concept.
 *
 * Key insight: "filtering" is a search mechanism to find nodes,
 * while "display options" define what to do with matches.
 */

/**
 * Condition field types for type-aware operator filtering
 */
export type ConditionFieldType = 'text' | 'number' | 'date' | 'boolean' | 'select';

/**
 * Operators for condition evaluation
 */
export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'greater_than'
  | 'less_than'
  | 'is_empty'
  | 'is_not_empty';

/**
 * Unified condition type for both filter and display rules
 */
export interface ViewCondition {
  id: string;
  field: string;
  fieldType?: ConditionFieldType;
  operator: ConditionOperator;
  value: string | number | boolean | Array<string | number | boolean> | null;
}

/**
 * Display rule types
 */
export type DisplayRuleType = 'color' | 'badge' | 'label';

/**
 * Badge positions on a node
 */
export type BadgePosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/**
 * Label positions relative to a node
 */
export type LabelPosition = 'above' | 'below' | 'inline';

/**
 * Filter effect options for non-matching nodes
 */
export type FilterEffect = 'none' | 'dim' | 'hide';

/**
 * Visibility options for the primary filter rule
 */
export interface VisibilityOptions {
  filterEffect: FilterEffect;
  showReportingLines: boolean;
}

/**
 * Color style options
 */
export interface ColorStyleOptions {
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
}

/**
 * Badge display options
 */
export interface BadgeOptions {
  icon: string;
  color: string;
  position: BadgePosition;
  tooltip?: string;
}

/**
 * Label display options
 */
export interface LabelOptions {
  template: string;
  position: LabelPosition;
  style?: {
    backgroundColor?: string;
    textColor?: string;
  };
}

/**
 * Heatmap display options for numeric field visualization
 */
export interface HeatmapOptions {
  field: string;
  minColor: string;
  maxColor: string;
  minValue?: number;
  maxValue?: number;
}

/**
 * Color by field options for category-based coloring
 */
export interface ColorByFieldOptions {
  field: string;
  /** Optional semantic palette keyed by the field's serialized value. */
  valueColors?: Record<string, string>;
}

/**
 * Combined display options for a view rule
 */
export interface DisplayOptions {
  visibility?: VisibilityOptions;
  colors?: ColorStyleOptions;
  badge?: BadgeOptions;
  label?: LabelOptions;
  heatmap?: HeatmapOptions;
  colorByField?: ColorByFieldOptions;
  secondaryReportsLens?: {
    enabled: boolean;
  };
  successorLens?: {
    visibility: 'selection' | 'all';
  };
  /**
   * Scope mode for lens rules.
   * - filter: applies lens to nodes matching active filters
   * - custom: applies lens using lens's own conditions
   */
  scopeMode?: 'filter' | 'custom';
}

/**
 * Unified view rule that combines search criteria with display options
 */
export interface ViewRule {
  id: string;
  name: string;
  conditions: ViewCondition[];
  logic: 'and' | 'or';
  enabled: boolean;
  display: DisplayOptions;
  order: number;
}

/**
 * Primary filter rule ID constant
 */
export const PRIMARY_FILTER_ID = 'primary-filter';

/** Canonical English name persisted for the primary filter rule */
export const PRIMARY_FILTER_CANONICAL_NAME = 'Primary Filter';

/**
 * Computed badge for rendering
 */
export interface ComputedBadge {
  icon: string;
  color: string;
  position: BadgePosition;
  tooltip?: string;
}

/**
 * Computed label for rendering
 */
export interface ComputedLabel {
  text: string;
  position: LabelPosition;
  style?: {
    backgroundColor?: string;
    textColor?: string;
  };
}

/**
 * Extended computed node style including display rules
 */
export interface ComputedNodeStyleWithRules {
  filterOpacity: number;
  filterVisible: boolean;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  badges: ComputedBadge[];
  labels: ComputedLabel[];
}

/**
 * Default empty computed style
 */
export const DEFAULT_COMPUTED_STYLE: ComputedNodeStyleWithRules = {
  filterOpacity: 1,
  filterVisible: true,
  badges: [],
  labels: [],
};

/**
 * Smart group / preset definition (saves a single filter configuration)
 */
export interface ViewRulePreset {
  id: string;
  name: string;
  description?: string;
  rule: Omit<ViewRule, 'id' | 'order'>;
  createdAt: number;
}

/**
 * Full preset that saves the entire rules configuration
 */
export interface FullPreset {
  id: string;
  name: string;
  rules: Array<Omit<ViewRule, 'id'>>;
  createdAt: number;
  requiredFields?: string[];
  /** Optional domain scope — limits the preset to folders/diagrams of this domain. */
  domain?: string;
}

/**
 * View rule kind - categorizes what type of visualization a rule represents
 */
export type ViewRuleKind = 'filter' | 'heatmap' | 'colorBy' | 'badge' | 'label' | 'highlight';

/**
 * Determine the kind of a view rule based on its display options
 */
export const getRuleKind = (rule: ViewRule): ViewRuleKind => {
  if (rule.display.heatmap) return 'heatmap';
  if (rule.display.colorByField) return 'colorBy';
  if (rule.display.badge) return 'badge';
  if (rule.display.label) return 'label';
  if (rule.display.colors && !rule.display.visibility) return 'highlight';
  return 'filter';
};

/**
 * Returns enabled filter-type rules that have at least one condition.
 * This includes the primary filter and any additional "Filter" rules.
 */
export const getActiveFilterRules = (rules: ViewRule[]): ViewRule[] =>
  rules
    .filter((rule) => getRuleKind(rule) === 'filter' && rule.enabled && rule.conditions.length > 0)
    .sort((a, b) => a.order - b.order);

const FILTER_EFFECT_PRIORITY: Record<FilterEffect, number> = {
  none: 0,
  dim: 1,
  hide: 2,
};

/**
 * Computes a single effective filter effect across all active filter rules.
 * Strongest effect wins: hide > dim > none.
 */
export const getEffectiveFilterEffect = (rules: ViewRule[]): FilterEffect => {
  const activeFilters = getActiveFilterRules(rules);
  if (activeFilters.length === 0) return 'none';

  let effective: FilterEffect = 'none';
  for (const rule of activeFilters) {
    const effect = rule.display.visibility?.filterEffect ?? 'dim';
    if (FILTER_EFFECT_PRIORITY[effect] > FILTER_EFFECT_PRIORITY[effective]) {
      effective = effect;
    }
  }
  return effective;
};

/**
 * Reporting lines are enabled if any active filter rule requests them.
 */
export const getEffectiveShowReportingLines = (rules: ViewRule[]): boolean =>
  getActiveFilterRules(rules).some(
    (rule) => rule.display.visibility?.showReportingLines ?? false
  );

/**
 * Helper to create a default primary filter rule
 */
export const createPrimaryFilterRule = (): ViewRule => ({
  id: PRIMARY_FILTER_ID,
  name: 'Primary Filter',
  conditions: [],
  logic: 'and',
  enabled: false,
  display: {
    visibility: {
      filterEffect: 'dim',
      showReportingLines: false,
    },
  },
  order: 0,
});

/**
 * Helper to create a new display rule with defaults
 */
export const createDisplayRule = (
  type: DisplayRuleType,
  name: string,
  order: number
): ViewRule => {
  const base: ViewRule = {
    id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name,
    conditions: [],
    logic: 'and',
    enabled: true,
    display: {},
    order,
  };

  switch (type) {
    case 'color':
      base.display.colors = {};
      break;
    case 'badge':
      base.display.badge = {
        icon: 'Star',
        color: '#3662E3',
        position: 'top-right',
      };
      break;
    case 'label':
      base.display.label = {
        template: '',
        position: 'below',
      };
      break;
  }

  return base;
};

/**
 * Helper to determine the type of a display rule
 */
export const getDisplayRuleType = (rule: ViewRule): DisplayRuleType | null => {
  if (rule.display.colors) return 'color';
  if (rule.display.badge) return 'badge';
  if (rule.display.label) return 'label';
  return null;
};

/**
 * Helper to check if a rule is the primary filter
 */
export const isPrimaryFilter = (rule: ViewRule): boolean => {
  return rule.id === PRIMARY_FILTER_ID;
};

/**
 * Factory function to create a heatmap rule
 */
export const createHeatmapRule = (field: string, order: number, minColor?: string, maxColor?: string): ViewRule => ({
  id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  name: 'Heatmap',
  conditions: [],
  logic: 'and',
  enabled: true,
  display: {
    heatmap: {
      field,
      minColor: minColor || '#dcfce7',
      maxColor: maxColor || '#dc2626',
    },
  },
  order,
});

/**
 * Factory function to create a colorBy rule
 */
export const createColorByRule = (field: string, order: number): ViewRule => ({
  id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  name: 'Color by Field',
  conditions: [],
  logic: 'and',
  enabled: true,
  display: {
    colorByField: { field },
  },
  order,
});

/**
 * Factory function to create a badge rule
 */
export const createBadgeRule = (order: number): ViewRule => ({
  id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  name: 'Badge',
  conditions: [],
  logic: 'and',
  enabled: true,
  display: {
    scopeMode: 'filter',
    badge: {
      icon: 'Star',
      color: '#f59e0b',
      position: 'top-right',
    },
  },
  order,
});

/**
 * Factory function to create a highlight rule
 */
export const createHighlightRule = (order: number): ViewRule => ({
  id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  name: 'Highlight',
  conditions: [],
  logic: 'and',
  enabled: true,
  display: {
    scopeMode: 'filter',
    colors: {
      backgroundColor: '#fef3c7',
      borderColor: '#f59e0b',
    },
  },
  order,
});

/**
 * Factory function to create a label rule
 */
export const createLabelRule = (order: number): ViewRule => ({
  id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  name: 'Label',
  conditions: [],
  logic: 'and',
  enabled: true,
  display: {
    label: {
      template: '',
      position: 'below',
    },
  },
  order,
});

/**
 * Factory function to create a filter rule
 */
export const createFilterRule = (name: string, order: number): ViewRule => ({
  id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  name,
  conditions: [],
  logic: 'and',
  enabled: true,
  display: {
    visibility: {
      filterEffect: 'dim',
      showReportingLines: false,
    },
  },
  order,
});

function escapeRegexForCopySuffix(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseFilterNameCopySuffix(
  name: string,
  copyWord: string,
): { root: string; num: number | null } | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  const localized = new RegExp(`^(.+?)\\s+${escapeRegexForCopySuffix(copyWord)}(?:\\s+(\\d+))?$`, 'iu');
  let m = trimmed.match(localized);
  if (!m && copyWord.toLowerCase() !== 'copy') {
    m = trimmed.match(/^(.+?)\s+Copy(?:\s+(\d+))?$/i);
  }
  if (!m) return null;
  const num = m[2] ? parseInt(m[2], 10) : 1;
  return { root: m[1].trim(), num: Number.isFinite(num) ? num : 1 };
}

function isPrimaryFilterRootForDuplicate(root: string, primaryLabel: string): boolean {
  const r = root.trim();
  if (!r) return false;
  if (r === PRIMARY_FILTER_CANONICAL_NAME || r.toLowerCase() === 'primary filter') return true;
  if (r === primaryLabel.trim()) return true;
  return false;
}

const DEFAULT_FILTER_CANONICAL_NAME = 'Filter';

function isDefaultFilterRootForDuplicate(root: string, fallbackFilterLabel: string): boolean {
  const r = root.trim();
  if (!r) return false;
  if (r === DEFAULT_FILTER_CANONICAL_NAME || r.toLowerCase() === 'filter') return true;
  if (r === fallbackFilterLabel.trim()) return true;
  return false;
}

type DiagramTranslateFn = (key: string, options?: { defaultValue?: string }) => unknown;

/**
 * Unique display name for a duplicated filter rule (locale-aware primary label + copy word).
 */
export function uniqueDuplicateFilterName(
  sourceName: string | undefined | null,
  existingNames: readonly string[],
  t: DiagramTranslateFn,
): string {
  const copyWord = String(t('assistant.filterDuplicateCopyWord', { defaultValue: 'Copy' })).trim();
  const primaryLabel = String(
    t('assistant.primaryFilterName', { defaultValue: PRIMARY_FILTER_CANONICAL_NAME }),
  ).trim();
  const fallbackFilter = String(t('assistant.ruleKindFilterDefault', { defaultValue: 'Filter' })).trim();

  const trimmed = (sourceName ?? '').trim();
  const parsed = parseFilterNameCopySuffix(trimmed, copyWord);

  let storedRoot: string;
  let seq: number | null;
  if (parsed) {
    storedRoot = parsed.root;
    seq = parsed.num;
  } else {
    storedRoot = trimmed || fallbackFilter;
    seq = null;
  }

  const displayRoot = isPrimaryFilterRootForDuplicate(storedRoot, primaryLabel)
    ? primaryLabel
    : isDefaultFilterRootForDuplicate(storedRoot, fallbackFilter)
      ? fallbackFilter
      : storedRoot;

  let proposed: string;
  if (seq !== null) {
    proposed = `${displayRoot} ${copyWord} ${seq + 1}`;
  } else {
    proposed = `${displayRoot} ${copyWord}`;
  }

  const taken = new Set(existingNames.map((s) => s.trim()).filter((s) => s.length > 0));
  if (!taken.has(proposed)) return proposed;

  let n = 2;
  let candidate = `${proposed} ${n}`;
  while (taken.has(candidate)) {
    n += 1;
    candidate = `${proposed} ${n}`;
  }
  return candidate;
}

/**
 * Legacy ActiveFilter type for backward compatibility with SmartGroupsStore
 */
export interface ActiveFilter {
  conditions: ViewCondition[];
  logic: 'and' | 'or';
}

/**
 * Legacy FilterCondition type alias for backward compatibility
 */
export type FilterCondition = ViewCondition;

/**
 * Legacy type aliases for backward compatibility
 */
export type FilterOperator = ConditionOperator;
export type FilterFieldType = ConditionFieldType;
export type FilterLogic = 'and' | 'or';
export type FilterMode = FilterEffect;

/**
 * Strips display config that names a field this viewer has no grant for.
 *
 * The rule itself survives — dropping the primary filter would silently
 * disable filtering — but its `heatmap` / `colorByField` are removed, which is
 * what the legend and the colouring read. Without this a persisted default
 * lens leaks the field name to viewers whose model is redacted: the heatmap
 * resolves nothing, renders 0–0, and still titles itself "Compensation Bands".
 */
export const stripBlockedDisplayFields = (
  rules: ViewRule[],
  blockedFields: readonly string[],
): ViewRule[] => {
  if (blockedFields.length === 0) return rules;
  const blocked = new Set(blockedFields.map((f) => f.trim().toLowerCase()));
  const isBlocked = (field: string | undefined): boolean =>
    typeof field === 'string' && blocked.has(field.trim().toLowerCase());

  let changed = false;
  const filtered = rules.map((rule) => {
    const heatmapBlocked = isBlocked(rule.display.heatmap?.field);
    const colorByBlocked = isBlocked(rule.display.colorByField?.field);
    if (!heatmapBlocked && !colorByBlocked) return rule;
    changed = true;
    const display = { ...rule.display };
    if (heatmapBlocked) delete display.heatmap;
    if (colorByBlocked) delete display.colorByField;
    return { ...rule, display };
  });
  return changed ? filtered : rules;
};
